// Tooltip — drop-in wrapper around DS `Tooltip` (US-053).
//
// DS-GAP-DESIGN (US-053):
//   The DS Tooltip already provides a token-driven, theme-aware tooltip with
//   focus + hover open semantics. The web app additionally needs a "drop-in
//   replacement for native `title` attributes" so any inline element can
//   migrate without rewriting the call site.
//
//   `<TitleTooltip>` is that helper: pass `label` (translatable) + `children`
//   and the wrapper handles Provider mounting + the `asChild` trigger handoff
//   so the consumer keeps full control over the trigger element.
//
//   We mount `TooltipProvider` here (idempotent — Radix's TooltipProvider can
//   stack) so consumers can call `<TitleTooltip>` from anywhere without
//   threading a Provider into App.tsx.

import { type ReactElement } from 'react';

import { Tooltip as DsTooltip, TooltipProvider } from '@barel/ui';

export interface TitleTooltipProps {
  /** Pre-localised tooltip label. Plain string (replaces native `title`). */
  label: string;
  /** Side of the trigger to render the tooltip. Defaults to `'top'`. */
  side?: 'top' | 'right' | 'bottom' | 'left';
  /** Disable the tooltip — handy for conditional title attribues. */
  disabled?: boolean;
  /** Single ReactElement that should host the tooltip (forwarded as `asChild`). */
  children: ReactElement;
}

/**
 * Drop-in replacement for native `title` attributes — wraps the trigger
 * element with the DS `Tooltip`. Mounts a local `TooltipProvider` so feature
 * code doesn't have to depend on a tree-level provider being mounted.
 *
 * Usage:
 * ```tsx
 * <TitleTooltip label={t('nav.accountsAlertLabel')}>
 *   <button aria-label="…" />
 * </TitleTooltip>
 * ```
 */
export function TitleTooltip({ label, side = 'top', disabled, children }: TitleTooltipProps) {
  if (disabled) return children;
  return (
    <TooltipProvider>
      <DsTooltip label={label} side={side}>
        {children}
      </DsTooltip>
    </TooltipProvider>
  );
}

export { DsTooltip as Tooltip, TooltipProvider };
